import { api } from '../config/api';

// Custom 3D brand buildings + the partner ↔ Outfino support chat.
// Shared contract: brand_buildings/CONTRACT.md, section "v3 API FINAL":
//   • partner routes are brand-scoped /brands/:brandId/... (a partner account
//     can own several brands; the dashboard passes the selected one);
//   • a building request becomes a support ticket (category brand_building) —
//     the support chat REUSES the existing /support-tickets system that the
//     mobile app and the admin Support page already use.
//
// The readers below normalise defensively (camelCase or snake_case, a bare
// array or a wrapped list) so a small server-side shape change degrades to
// "fewer fields" rather than to a blank page.

/// Gross starting price, in HUF, when the API does not say.
export const BRAND_BUILDING_PRICE_FALLBACK = 800000;
export const BRAND_BUILDING_TOPIC = 'brand_building';
/// API limits on POST /brands/:id/building-requests.
export const REQUEST_DESCRIPTION_MAX = 4000;
export const REQUEST_LINKS_MAX = 10;

const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] != null) return o[k];
  return null;
};

export function normalizeBuilding(b) {
  if (!b || typeof b !== 'object') return null;
  const lod0Url = pick(b, 'lod0Url', 'lod0_url');
  if (!lod0Url) return null;
  return {
    brandId: pick(b, 'brandId', 'brand_id'),
    brandName: pick(b, 'brandName', 'brand_name') || '',
    brandLogoUrl: pick(b, 'brandLogoUrl', 'brand_logo_url'),
    version: pick(b, 'version') ?? 1,
    lod0Url,
    lod1Url: pick(b, 'lod1Url', 'lod1_url'),
    lod2Url: pick(b, 'lod2Url', 'lod2_url'),
    thumbnailUrl: pick(b, 'thumbnailUrl', 'thumbnail_url'),
    updatedAt: pick(b, 'updatedAt', 'updated_at'),
  };
}

function normalizePrice(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : BRAND_BUILDING_PRICE_FALLBACK;
}

/// A support ticket (or the `openThread` summary) → the chat list's thread.
/// `category` is the topic ('brand_building' | 'general' | legacy values).
export function normalizeThread(t) {
  if (!t || typeof t !== 'object' || t.id == null) return null;
  const last = pick(t, 'lastMessage', 'last_message');
  const lastText = typeof last === 'string' ? last : pick(last, 'message', 'body', 'text');
  return {
    id: String(t.id),
    topic: pick(t, 'category', 'topic') || 'general',
    status: pick(t, 'status') || 'open',
    lastMessageAt:
      pick(t, 'lastMessageAt', 'last_message_at') ||
      pick(last, 'createdAt', 'created_at') ||
      pick(t, 'updatedAt', 'updated_at', 'createdAt', 'created_at'),
    createdAt: pick(t, 'createdAt', 'created_at'),
    lastMessage: lastText || pick(t, 'message') || null,
    unread: pick(t, 'hasUnread', 'has_unread') ? 1 : Number(pick(t, 'unreadCount', 'unread_count') || 0),
  };
}

/// A ticket message. senderType 'admin' is the Outfino side, 'user' the
/// partner's; the UI only needs "outfino" vs "partner".
export function normalizeMessage(m) {
  if (!m || typeof m !== 'object') return null;
  const raw = pick(m, 'senderType', 'sender_type') || 'user';
  const sender = pick(m, 'sender') || {};
  const senderName = pick(sender, 'firstName', 'first_name', 'username') || null;
  return {
    id: String(m.id ?? `${pick(m, 'createdAt', 'created_at')}:${raw}`),
    senderType: raw === 'admin' || raw === 'outfino' ? 'outfino' : 'partner',
    senderName,
    body: pick(m, 'message', 'body', 'text') || '',
    imageUrl: pick(m, 'imageUrl', 'image_url'),
    createdAt: pick(m, 'createdAt', 'created_at'),
  };
}

const listOf = (res, ...keys) => {
  if (Array.isArray(res)) return res;
  for (const k of keys) if (Array.isArray(res?.[k])) return res[k];
  return [];
};

// ---- Buildings ----

/// GET /brands/:brandId/building → { building, priceFromHuf, openThread,
/// storefront }. `openThread` = the brand's latest OPEN brand_building support
/// ticket; `storefront` = the raw default-store fields the answer carries
/// (storefrontLogoShape — see resolveStorefront), null when there are none.
export async function fetchBrandBuildingInfo(brandId) {
  const res = await api(`/brands/${brandId}/building`);
  return {
    building: normalizeBuilding(res?.building),
    priceFromHuf: normalizePrice(pick(res, 'priceFromHuf', 'price_from_huf')),
    openThread: normalizeThread(pick(res, 'openThread', 'open_thread')),
    storefront: storefrontFieldsOf(res),
  };
}

/// GET /brand-buildings?cursor=&limit= — every live custom building, newest
/// first, one page at a time (cursor is opaque).
export async function fetchBrandBuildings({ cursor = null, limit = 24 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  const res = await api(`/brand-buildings?${params.toString()}`);
  return {
    items: listOf(res, 'items', 'data').map(normalizeBuilding).filter(Boolean),
    nextCursor: pick(res, 'nextCursor', 'next_cursor'),
    total: pick(res, 'total'),
    priceFromHuf: normalizePrice(pick(res, 'priceFromHuf', 'price_from_huf')),
  };
}

/// Reference links as the API accepts them: trimmed, non-empty, http(s)
/// (a bare "pinterest.com/…" gets https://), at most REQUEST_LINKS_MAX.
export function cleanLinks(links) {
  return (links || [])
    .map((l) => String(l || '').trim())
    .filter(Boolean)
    .map((l) => (/^https?:\/\//i.test(l) ? l : `https://${l}`))
    .slice(0, REQUEST_LINKS_MAX);
}

/// POST /brands/:brandId/building-requests → the new support ticket. The API
/// writes the chat's first message itself (description, then the links and
/// the contact email). 409 brand_building_request_open = the brand already has
/// an open one — see existingThreadFromError.
export async function createBuildingRequest(brandId, { description, referenceLinks, contactEmail }) {
  const links = cleanLinks(referenceLinks);
  const email = String(contactEmail || '').trim();
  const res = await api(`/brands/${brandId}/building-requests`, {
    method: 'POST',
    body: {
      description: description.trim(),
      ...(links.length ? { referenceLinks: links } : {}),
      ...(email ? { contactEmail: email } : {}),
    },
  });
  return normalizeThread(res?.ticket ?? res);
}

/// The open ticket a 409 points at: `{ code, message, details: { ticketId } }`.
export function existingThreadFromError(err) {
  const d = err?.data;
  const id = pick(d?.details, 'ticketId', 'ticket_id') || pick(d, 'ticketId') || pick(d?.ticket, 'id');
  return id != null ? { id: String(id) } : null;
}

// ---- Support chat (existing /support-tickets) ----

/// All of this brand's tickets (team-visible), newest first.
export async function fetchSupportThreads(brandId) {
  const q = new URLSearchParams({ brandId: String(brandId) }).toString();
  const res = await api(`/support-tickets?${q}`);
  return listOf(res, 'tickets', 'items', 'data').map(normalizeThread).filter(Boolean);
}

/// A new general conversation for this brand.
export async function createSupportThread(brandId, { message, category = 'general' }) {
  const res = await api('/support-tickets', {
    method: 'POST',
    body: { type: 'support', message, category, brandId },
  });
  return normalizeThread(res?.ticket ?? res);
}

export async function fetchSupportMessages(threadId) {
  const res = await api(`/support-tickets/${encodeURIComponent(threadId)}/messages`);
  return listOf(res, 'messages', 'items', 'data').map(normalizeMessage).filter(Boolean);
}

/// Resolves `{ message, newTicket }`. Writing into a CLOSED ticket makes the
/// API open a new one (same brand + category) — `newTicket` is then set and
/// the conversation continues there.
export async function sendSupportMessage(threadId, message) {
  const res = await api(`/support-tickets/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    body: { message },
  });
  return {
    message: normalizeMessage(res?.message ?? res),
    newTicket: normalizeThread(pick(res, 'newTicket', 'new_ticket')),
  };
}

export async function markSupportRead(threadId) {
  return api(`/support-tickets/${encodeURIComponent(threadId)}/read`, { method: 'PUT', body: {} });
}

// ---- Default store: logo shape + closed state ----
// brand_stores/DEFAULT_STORE_CONTRACT.md. Every brand object the app uses for
// storefronts carries
//   storefrontLogoShape    'round' | 'square' | 'rect'   (absent → 'rect')
//   storefrontClosed       bool                          (absent → open)
//   storefrontClosedReason 'inactive' | 'no_shopify' | null
// The logo shape only matters on the default store; a live custom building
// ignores it.

export const LOGO_SHAPES = ['round', 'square', 'rect'];
export const DEFAULT_LOGO_SHAPE = 'rect';
export const CLOSED_REASONS = ['inactive', 'no_shopify'];

export function normalizeLogoShape(v) {
  const s = typeof v === 'string' ? v.trim().toLowerCase() : '';
  return LOGO_SHAPES.includes(s) ? s : DEFAULT_LOGO_SHAPE;
}

const SF_KEYS = {
  logoShape: ['storefrontLogoShape', 'storefront_logo_shape'],
  closed: ['storefrontClosed', 'storefront_closed'],
  closedReason: ['storefrontClosedReason', 'storefront_closed_reason'],
};

/// Just the storefront fields an object actually carries (camelCase or
/// snake_case), or null when it carries none.
export function storefrontFieldsOf(o) {
  if (!o || typeof o !== 'object') return null;
  const out = {};
  for (const [k, keys] of Object.entries(SF_KEYS)) {
    const key = keys.find((x) => x in o);
    if (key) out[k] = o[key];
  }
  return Object.keys(out).length ? out : null;
}

/// { logoShape, closed, closedReason } off a brand-ish object, with the wire
/// defaults for absent fields (rect, open). An unknown reason on a closed
/// store stays null — the UI then explains it generically.
export function readStorefront(o) {
  return resolveStorefront(o);
}

/// Merge the storefront state from several payloads, per field. Where the API
/// sends what (DEFAULT_STORE_CONTRACT.md v2):
///   GET /brands/:id (`detail`)         shape + closed + reason, always
///   GET /brands/:id/building (`info`)  shape only
///   /user/me stores[] (`brand`)        whatever it carries (fallback)
/// The shape prefers /building (the section's own fresh read), the closed
/// state the brand detail.
export function resolveStorefront(brand, info = null, detail = null) {
  const b = storefrontFieldsOf(brand) || {};
  const i = info?.storefront || {};
  const d = storefrontFieldsOf(detail) || {};
  const first = (k, ...srcs) => {
    for (const src of srcs) if (src[k] != null) return src[k];
    return null;
  };
  const closed = first('closed', d, i, b) === true;
  const reason = first('closedReason', d, i, b);
  return {
    logoShape: normalizeLogoShape(first('logoShape', i, d, b)),
    closed,
    closedReason: closed && CLOSED_REASONS.includes(reason) ? reason : null,
  };
}

/// Save the default store's logo shape through the brand edit endpoint,
/// PUT /brands/:id { storefrontLogoShape } (permission editBrand; 400
/// invalid_parameters on an unknown shape). In the Shopify-embedded build the
/// same call maps to PUT /shopify/admin/brand. Resolves to the stored shape.
export async function updateStorefrontLogoShape(brandId, shape) {
  const body = { storefrontLogoShape: normalizeLogoShape(shape) };
  const res = await api(`/brands/${brandId}`, { method: 'PUT', body });
  const saved = pick(res, 'storefrontLogoShape', 'storefront_logo_shape') ?? pick(res?.brand, 'storefrontLogoShape', 'storefront_logo_shape');
  // An endpoint that answers with the brand but a different shape did not
  // take the change (e.g. an API build without the field) — surface that as a
  // failure so the UI rolls back.
  if (saved != null && normalizeLogoShape(saved) !== body.storefrontLogoShape) {
    const err = new Error('storefront_logo_shape_not_saved');
    err.status = 0;
    throw err;
  }
  return body.storefrontLogoShape;
}

// ---- Formatting ----

/// "bruttó 800 000 Ft-tól" / "from HUF 800,000 (gross)" — the amount is
/// formatted per locale, the words come from i18n (`building.priceFrom`).
export function formatHuf(amount, lang) {
  return new Intl.NumberFormat(lang === 'hu' ? 'hu-HU' : 'en-US', { maximumFractionDigits: 0 }).format(amount);
}
